import { ToastrService } from 'ngx-toastr';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { EquipmentComponent } from './../equipment.component';
import { Component, HostListener, OnInit, TemplateRef } from '@angular/core';
import { TextModel } from '../../../models/text';
import { TranslateService } from '@ngx-translate/core';
import { TextService } from '../../../services/text.service';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import {
  Object3D,
  Material,
  AmbientLight,
  CircleGeometry,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  PMREMGenerator,
  RepeatWrapping,
  Scene,
  Texture,
  TextureLoader,
  UnsignedByteType,
  WebGLRenderer,
  Vector3,
  MeshPhongMaterial,
  Group
} from 'three';
import { gsap } from 'gsap';
import { forkJoin } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfigService } from '../../../services/config.service';
import { AuthService } from 'src/services/guard/auth.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute } from '@angular/router';

enum Positions {
  top = 'Top',
  bottom = 'Bottom',
  common = 'Common',
  default = 'Default'
}

@Component({
  selector: 'app-equipment-full',
  templateUrl: './equipment-full.component.html',
  styleUrls: ['./equipment-full.component.scss']
})
export class EquipmentFullComponent extends EquipmentComponent implements OnInit {
  private defaultPosition = {
    camera: { x: -2.770761007426036, y: 1.6153347608223778, z: 5.249604854069164 },
    controls: { x: -2.3731832062697884, y: 0.8098953871206612, z: -0.6218083951180616 }
  };

  private commonPosition = {
    camera: { x: 7.89, y: 1.99, z: 5.94 },
    controls: { x: 5.50, y: 0.17, z: -2.31 }
  };

  private upPosition = {
    camera: { x: -3.7760848462935908, y: 2.2475243542342103, z: 4.6817115585578595 },
    controls: { x: -2.04, y: 0.3, z: -1.98 }
  };

  private downPosition = {
    camera: { x: 0.4320352670280496, y: 0.987736943875054, z: 6.047850820495081 },
    controls: { x: -1.8137466949845902, y: 1.0296, z: 1.2998032655910174 }
  };

  Positions = Positions;
  currentPosition = Positions.default;
  displayRatio: number;

  // 3D
  model: Group;
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  loader: GLTFLoader;
  pmremGenerator: PMREMGenerator;
  textureLoader: TextureLoader;
  controls: OrbitControls;
  circles: Mesh<CircleGeometry, MeshPhongMaterial>[] = [];
  environment: any;


  // HTML
  canvas: HTMLCanvasElement;
  container: JQuery<HTMLElement>;
  infoDefault: JQuery<HTMLElement>;
  infoBottom: JQuery<HTMLElement>;
  infoTop: JQuery<HTMLElement>;
  infoCommon: JQuery<HTMLElement>;
  labels = ['label-common', 'label-bottom', 'label-top'];

  modalRef: BsModalRef;
  textModel: TextModel;

  constructor(
    translate: TranslateService,
    textService: TextService,
    private spinner: NgxSpinnerService,
    protected config: ConfigService,
    authService: AuthService,
    private modalService: BsModalService,
    private toast: ToastrService,
    deviceDetectorService: DeviceDetectorService,
    route: ActivatedRoute
  ) {
    super(translate, textService, authService, deviceDetectorService, route);
    this.environment = {
      id: 1,
      name: 'Sun',
      path: `${config.config.resourcesUrl}3D/world/kloofendal_48d_partly_cloudy_1k.hdr`,
      format: '.hdr'
    };
  }

  ngOnInit() {
    super.ngOnInit();
    this.init3D();
  }

  init3D() {
    // HTML
    this.canvas = $('#c')[0] as HTMLCanvasElement;
    this.container = $('.container-3d');
    this.infoDefault = $('.default');
    this.infoTop = $('.top');
    this.infoBottom = $('.bottom');
    this.infoCommon = $('.common');

    this.displayRatio = this.container.width() >= 1723 ? 1 : 1740 / this.container.width() - 0.2;

    // Scene
    this.scene = new Scene();
    const ambient = new AmbientLight(0xAAAAAA, 4);
    this.scene.add(ambient);

    // Renderer
    this.renderer = new WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true });
    this.renderer.physicallyCorrectLights = true;
    this.renderer.setSize(this.container.width(), this.container.height());
    this.container.append(this.renderer.domElement);

    // Camera
    this.camera = new PerspectiveCamera(75, this.container.width() / this.container.height(), 0.1, 1000);
    this.camera.position.set(this.defaultPosition.camera.x, this.defaultPosition.camera.y, this.defaultPosition.camera.z);
    this.camera.aspect = this.container.width() / this.container.height();
    this.camera.updateProjectionMatrix();
    this.scene.add(this.camera);

    // Loader
    this.loader = new GLTFLoader();
    const draco = new DRACOLoader();
    draco.setDecoderPath(`${this.config.config.resourcesUrl}3D/libs/draco/`);
    this.loader.setDRACOLoader(draco);

    // Generator
    this.pmremGenerator = new PMREMGenerator(this.renderer);
    this.pmremGenerator.compileEquirectangularShader();

    // TextureLoader
    this.textureLoader = new TextureLoader();

    // Controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enabled = false;
    this.controls.target.set(this.defaultPosition.controls.x / this.displayRatio, this.defaultPosition.controls.y / this.displayRatio, this.defaultPosition.controls.z / this.displayRatio);
    this.controls.update();

    this.loadScene();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this.camera && this.renderer && this.model) {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      this.renderer.setPixelRatio(window.devicePixelRatio); this.renderer.render(this.scene, this.camera);
    }
  }

  loadScene() {
    this.spinner.show();
    this.loader.load(`${this.config.config.resourcesUrl}3D/models/skalaki.gltf`, gltf => {
      this.model = gltf.scene;
      let v = new Vector3();
      this.model.getWorldScale(v);
      this.model.scale.set(v.x / this.displayRatio, v.y / this.displayRatio, v.z / this.displayRatio);

      const terrasseTexture = this.textureLoader.load('../../../assets/3D/textures/seamless-tiles.png');
      terrasseTexture.wrapS = RepeatWrapping;
      terrasseTexture.wrapT = RepeatWrapping;
      terrasseTexture.flipY = false;
      terrasseTexture.repeat.set(80, 80);
      const terrasseMat = new MeshStandardMaterial({ map: terrasseTexture, roughness: 0.2, envMapIntensity: 0.1, side: 2 });

      const murMat = new MeshStandardMaterial({ color: 0xFFFFFF, roughness: 0.8, envMapIntensity: 0.2, side: 2 });
      const rembardeMat = new MeshStandardMaterial({ color: 0xD1D1D1, roughness: 0.8, envMapIntensity: 0.3, side: 2 });
      const chassisMat = new MeshStandardMaterial({ color: 0xF5F5F5, roughness: 0.2, envMapIntensity: 0.4, side: 2 });
      const porteMat = new MeshStandardMaterial({ color: 0xE7E7E7, roughness: 0.2, envMapIntensity: 0.35, side: 2 });
      const tuilesMat = new MeshStandardMaterial({ color: 0xFFC680, roughness: 0.5, envMapIntensity: 0.15, side: 2 });
      const boisMat = new MeshStandardMaterial({ color: 0x915954, roughness: 1, envMapIntensity: 0.2, side: 2 });

      const matRequests = [
        this.updateMaterial(this.model, 'Terrasse', terrasseMat),
        this.updateMaterial(this.model, 'Murs', murMat),
        this.updateMaterial(this.model, 'Chassis', chassisMat),
        this.updateMaterial(this.model, 'Rembarde', rembardeMat),
        this.updateMaterial(this.model, 'Porte', porteMat),
        this.updateMaterial(this.model, 'Tuiles', tuilesMat),
        this.updateMaterial(this.model, 'Bois', boisMat),
        this.updateEnvironment()
      ];

      forkJoin(matRequests).subscribe(() => {
        this.spinner.hide();
        this.scene.add(this.model);
        this.addCircles();
        this.animate();
        this.updateLabelsPosition();
        $('.label').css('display', 'block');
      });

    }, undefined,
      e => console.error(e)
    );
  }

  private animate = () => {
    const c = this.renderer.domElement;
    if (this.resizeRendererToDisplaySize(this.renderer)) {
      this.camera.aspect = c.clientWidth / c.clientHeight;
      this.camera.updateProjectionMatrix();
    }

    requestAnimationFrame(this.animate);
    this.renderer.render(this.scene, this.camera);

    // console.log('target');
    // console.log(this.controls.target);
    // console.log('camera');
    // console.log(this.camera.position);
  }

  //#region moves
  showDefault() {
    if (this.currentPosition === Positions.default) {
      return;
    }
    $('.wire-btn').css('display', 'none');
    $('.label').css('display', 'none');
    $('.label').removeClass('selected');
    $('.info').css('opacity', '0');
    $('.info').css('z-index', '0');
    gsap.to(this.controls.target, {
      duration: 2,
      x: this.defaultPosition.controls.x / this.displayRatio,
      y: this.defaultPosition.controls.y / this.displayRatio,
      z: this.defaultPosition.controls.z / this.displayRatio,
      onUpdate: () => {
        this.controls.update();
      }
    });
    gsap.to(this.camera.position, {
      duration: 2,
      x: this.defaultPosition.camera.x,
      y: this.defaultPosition.camera.y,
      z: this.defaultPosition.camera.z,
      onUpdate: () => {
        this.controls.update();
      },
      onComplete: () => {
        this.infoDefault.css('opacity', '1');
        this.infoDefault.css('z-index', '1');
        this.updateLabelsPosition();
        this.currentPosition = Positions.default;
        $('.wire-btn').css('display', 'block');
      }
    });
  }

  showTop() {
    if (this.currentPosition === Positions.top) {
      return;
    }
    this.currentPosition = Positions.top;
    $('.label').css('display', 'none');
    $('.label').removeClass('selected');
    $('.info').css('opacity', '0');
    $('.info').css('z-index', '0');
    $('.wire-btn').css('display', 'none');
    gsap.to(this.controls.target, {
      duration: 2,
      x: this.upPosition.controls.x / this.displayRatio,
      y: this.upPosition.controls.y / this.displayRatio,
      z: this.upPosition.controls.z / this.displayRatio,
      onUpdate: () => {
        this.controls.update();
      }
    });
    gsap.to(this.camera.position, {
      duration: 2,
      x: this.upPosition.camera.x,
      y: this.upPosition.camera.y,
      z: this.upPosition.camera.z,
      onUpdate: () => {
        this.controls.update();
      },
      onComplete: () => {
        this.infoTop.css('opacity', '1');
        this.infoTop.css('z-index', '1');
        $('.label-top').addClass('selected');
        this.updateLabelsPosition();
        $('.wire-btn').css('display', 'block');
      }
    });
  }

  showBottom() {
    if (this.currentPosition === Positions.bottom) {
      return;
    }
    this.currentPosition = Positions.bottom;
    $('.label').css('display', 'none');
    $('.label').removeClass('selected');
    $('.info').css('opacity', '0');
    $('.info').css('z-index', '0');
    $('.wire-btn').css('display', 'none');
    gsap.to(this.controls.target, {
      duration: 2,
      x: this.downPosition.controls.x / this.displayRatio,
      y: this.downPosition.controls.y / this.displayRatio,
      z: this.downPosition.controls.z / this.displayRatio,
      onUpdate: () => {
        this.controls.update();
      },
      onComplete: () => {
        this.infoBottom.css('opacity', '1');
        this.infoBottom.css('z-index', '1');
        $('.label-bottom').addClass('selected');
        $('.wire-btn').css('display', 'block');
        this.updateLabelsPosition();
      }
    });
    gsap.to(this.camera.position, {
      duration: 2,
      x: this.downPosition.camera.x,
      y: this.downPosition.camera.y,
      z: this.downPosition.camera.z,
      onUpdate: () => {
        this.controls.update();
      }
    });
  }

  showCommon() {
    if (this.currentPosition === Positions.common) {
      return;
    }
    this.currentPosition = Positions.common;
    $('.label').css('display', 'none');
    $('.label').removeClass('selected');
    $('.info').css('opacity', '0');
    $('.info').css('z-index', '0');
    $('.wire-btn').css('display', 'none');
    gsap.to(this.controls.target, {
      duration: 2,
      x: this.commonPosition.controls.x / this.displayRatio,
      y: this.commonPosition.controls.y / this.displayRatio,
      z: this.commonPosition.controls.z / this.displayRatio,
      onUpdate: () => {
        this.controls.update();
      }
    });
    gsap.to(this.camera.position, {
      duration: 2,
      x: this.commonPosition.camera.x,
      y: this.commonPosition.camera.y,
      z: this.commonPosition.camera.z,
      onUpdate: () => {
        this.controls.update();
      },
      onComplete: () => {
        this.infoCommon.css('opacity', '1');
        this.infoCommon.css('z-index', '1');
        $('.label-common').addClass('selected');
        $('.wire-btn').css('display', 'block');
        this.updateLabelsPosition();
      }
    });
  }
  //#endregion

  //#region UTILS

  private updateEnvironment(): Promise<null> {
    return new Promise((resolve, reject) => {
      this.getCubeMapTexture().then(envMap => {
        this.scene.environment = envMap;
        resolve(null);
      });
    });
  }

  private getCubeMapTexture(): Promise<Texture | null> {
    const { path } = this.environment;

    // no envmap
    if (!path) {
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      new RGBELoader()
        .setDataType(UnsignedByteType)
        .load(path, (texture) => {
          const envMap = this.pmremGenerator.fromEquirectangular(texture).texture;
          this.pmremGenerator.dispose();
          resolve(envMap);
        }, undefined, reject);
    });
  }

  private getMaterialByName(obj: any, name: string): Promise<any> {
    return new Promise((resolve, reject) => {
      obj.traverse(c => {
        if (c.material && c.material.name === name) {
          resolve(c.material);
        }
      });
    });
  }

  private updateMaterial(obj: Object3D, materialName: string, newMaterial: Material): Promise<Material> {
    return new Promise((resolve, reject) => {
      this.getMaterialByName(obj, materialName).then((m: Material) => {
        m.copy(newMaterial);
        resolve(m);
      });
    });
  }

  private resizeRendererToDisplaySize(r) {
    const canvas = r.domElement;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const needResize = canvas.width !== width || canvas.height !== height;
    if (needResize) {
      r.setSize(width, height, false);
    }
    return needResize;
  }

  private addCircle(x: number, y: number, z: number) {
    const circleMat = new MeshPhongMaterial({ opacity: 0, transparent: true });

    const geometry = new CircleGeometry(.1, 32);
    const circle = new Mesh(geometry, circleMat);
    circle.position.set(x, y, z);

    this.scene.add(circle);
    this.circles.push(circle);
  }

  private addCircles() {
    // Common
    this.addCircle(2 / this.displayRatio, 2 / this.displayRatio, 1.5 / this.displayRatio);

    // Bottom
    this.addCircle(-0.5 / this.displayRatio, 0.5 / this.displayRatio, 2.6 / this.displayRatio);

    // Top
    this.addCircle(-2 / this.displayRatio, 2 / this.displayRatio, 1.5 / this.displayRatio);
  }

  private updateLabelsPosition() {
    for (let i = 0; i < this.labels.length; i++) {
      const label = $('.' + this.labels[i]);
      const circle = this.circles[i];

      // get the position of the center of the cube
      circle.updateWorldMatrix(true, false);
      const tempV = circle.getWorldPosition(new Vector3());

      // get the normalized screen coordinate of that position
      // x and y will be in the -1 to +1 range with x = -1 being
      // on the left and y = -1 being on the bottom
      tempV.project(this.camera);

      // convert the normalized position to CSS coordinates
      const x = (tempV.x * .5 + .5) * this.canvas.clientWidth;
      const y = (tempV.y * -.5 + .5) * this.canvas.clientHeight;

      // move the elem to that position
      label.css('transform', `translate(-50%, -50%) translate(${x}px,${y}px)`);
    }
    $('.label').css('display', 'block');
  }

  //#endregion

  updateText(text: TextModel, template: TemplateRef<any>) {
    this.textModel = new TextModel();
    Object.assign(this.textModel, text);
    this.modalRef = this.modalService.show(template);
  }

  saveText() {
    this.textService.saveText(this.textModel).subscribe(
      () => this.toast.success('Text saved!'),
      err => {
        console.error(err);
        this.toast.error('Error while saving the text...');
      },
      () => {
        super.ngOnInit();
        this.modalRef.hide();
      }
    );
  }
}
